import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useSessionClaims } from '@/hooks/use-session-claims';
import {
  useCryptoOnchainChainHealth,
  useCryptoOnchainGasWallets,
  useCryptoOnchainPayouts,
  useCryptoOnchainPlatformMerchants,
  useCryptoOnchainPlatformPayments,
  useCryptoOnchainPlatformSettings,
} from '@/lib/api/crypto-onchain-platform';

import { AvailabilityBanner } from './components/AvailabilityBanner';
import { ChainHealthPanel } from './components/ChainHealthPanel';
import { GasWalletsPanel } from './components/GasWalletsPanel';
import { MerchantsTable } from './components/MerchantsTable';
import { PayoutsTable } from './components/PayoutsTable';
import { PlatformTestnetWalletsPanel } from './components/PlatformTestnetWalletsPanel';
import { PlatformWalletsPanel } from './components/PlatformWalletsPanel';
import { RecentPaymentsTable } from './components/RecentPaymentsTable';
import { RevenueShareForm } from './components/RevenueShareForm';
import type { PayoutStage } from './components/types';

/** How many payments the page shows; also what the copy says. */
const RECENT_PAYMENTS_LIMIT = 50;

function LoadingCard() {
  return (
    <Card>
      <CardContent className="flex h-40 items-center justify-center">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </CardContent>
    </Card>
  );
}

/**
 * Staff-only operations page for on-chain crypto payments (crypto-api): the
 * platform wallets that collect the revenue share, the default share, the gas
 * wallets that pay for token sweeps, every merchant enabled with its override,
 * the latest payments with what was settled to the platform, and every sweep.
 *
 * Platform-wide and not org-scoped, like the Apple Pay integrator page. The
 * layout already gates the area; the page re-checks so a direct link to it can
 * never render for a merchant.
 */
export default function ManageCryptoPaymentsPage() {
  const { t } = useTranslation('manageCryptoPayments');
  const { isAccountManager, isLoading: isLoadingRole } = useAccountManager();
  const { isSuperAdmin } = useSessionClaims();

  const canAccess = isAccountManager || isSuperAdmin;

  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useCryptoOnchainPlatformSettings();
  const {
    data: merchants,
    isLoading: isLoadingMerchants,
    error: merchantsError,
  } = useCryptoOnchainPlatformMerchants();
  const {
    data: payments,
    isLoading: isLoadingPayments,
    error: paymentsError,
  } = useCryptoOnchainPlatformPayments(undefined, RECENT_PAYMENTS_LIMIT);
  const isConfigured = settings?.configured === true;
  const chainHealth = useCryptoOnchainChainHealth();
  const gasWallets = useCryptoOnchainGasWallets();
  const [payoutStage, setPayoutStage] = useState<PayoutStage | undefined>(undefined);
  const payouts = useCryptoOnchainPayouts({ stage: payoutStage });

  // A failed query and an empty platform look the same on screen; say which.
  const loadError = settingsError ?? merchantsError ?? paymentsError;
  const defaultSharePercent = settings?.defaultSharePercent ?? null;

  if (isLoadingRole) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t('page.checkingPermissions')}</span>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-semibold">{t('page.accessDeniedTitle')}</h2>
        <p className="text-muted-foreground">{t('page.accessDeniedDescription')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('page.title')} description={t('page.description')} />

      {loadError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('page.loadFailedTitle')}</AlertTitle>
          <AlertDescription>
            <p>{t('page.loadFailed')}</p>
            <p className="mt-1 font-mono text-xs">{loadError.message}</p>
          </AlertDescription>
        </Alert>
      )}

      {settings && <AvailabilityBanner settings={settings} />}

      {isLoadingSettings ? (
        <LoadingCard />
      ) : (
        <>
          <PlatformWalletsPanel settings={settings ?? null} />
          {/* Re-keyed on the saved wallets so a later open starts from the server's answer. */}
          <PlatformTestnetWalletsPanel
            key={JSON.stringify(settings?.testnetWallets ?? null)}
            settings={settings ?? null}
          />
          {/* Re-keyed on the saved value so the form restarts from the server's
              number after a save, without an effect copying props into state. */}
          <RevenueShareForm key={defaultSharePercent ?? 'unknown'} settings={settings ?? null} />
        </>
      )}

      {isConfigured && (
        <ChainHealthPanel
          chains={chainHealth.data}
          isLoading={chainHealth.isLoading}
          isFetching={chainHealth.isFetching}
          errorMessage={chainHealth.error?.message ?? null}
          onRecheck={() => void chainHealth.refetch()}
        />
      )}

      {isConfigured && (
        <GasWalletsPanel
          wallets={gasWallets.data}
          isLoading={gasWallets.isLoading}
          errorMessage={gasWallets.error?.message ?? null}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('merchants.title')}</CardTitle>
          <CardDescription>{t('merchants.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingMerchants ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : (
            <MerchantsTable merchants={merchants ?? []} defaultSharePercent={defaultSharePercent} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('payments.title')}</CardTitle>
          <CardDescription>{t('payments.description', { limit: RECENT_PAYMENTS_LIMIT })}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingPayments ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : (
            <RecentPaymentsTable payments={payments ?? []} />
          )}
        </CardContent>
      </Card>

      {isConfigured && (
        <Card>
          <CardHeader>
            <CardTitle>{t('payouts.title')}</CardTitle>
            <CardDescription>{t('payouts.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {payouts.isLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              </div>
            ) : payouts.error ? (
              <p className="text-destructive text-sm">{t('payouts.loadFailed', { error: payouts.error.message })}</p>
            ) : (
              <PayoutsTable
                payouts={payouts.data?.items ?? []}
                stage={payoutStage}
                onStageChange={setPayoutStage}
                canRetry={isSuperAdmin}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
